import { NOYAU_TEXTS_QUERY_KEY } from '@/components/admin/reglages/noyau-texts-editor';
import { useAdminSession } from '@/lib/admin-session';
import { fetchNoyauTexts, noyauTextsApi } from '@/lib/api';
import type { NoyauTextsSource } from '@/lib/noyau-preview';
import type { VersionPin } from '@matter_extractor/shared';
import { NOYAU_TEXTS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';

/**
 * The texts of the noyaux as the editors of an experience read them (noyau.md
 * §3.5, N20; issue #151): the detail of the singleton, and the version a
 * reference PINS when that is not the development one - the two contents a
 * reference can read, whatever its pin (`noyauTextsAt` picks).
 *
 * Read by the SCREEN - the page editor, the full-screen preview - and handed
 * down, the régime of the protocol library the page editor reads for its body:
 * the body and the reader fetch nothing. The detail is read under the key
 * Réglages > Noyaux writes (`NOYAU_TEXTS_QUERY_KEY`), so a version saved there
 * shows here without a reload; a pinned version under the key of its « Voir ».
 * A 404 is the empty state of §6.4 and reads as null.
 */
export function useNoyauTextsSource(enabled = true, pin: VersionPin = 'latest'): NoyauTextsSource {
  const { email } = useAdminSession();
  const detail = useQuery({
    queryKey: [NOYAU_TEXTS_QUERY_KEY, email, NOYAU_TEXTS_ID],
    queryFn: fetchNoyauTexts,
    enabled,
    retry: false,
  });

  const read = detail.data;
  // The version a NUMBER names, when it is not the development one the detail
  // already carries; null for `latest` and for a number the texts never had.
  const pinned =
    read == null || pin === 'latest' || pin === read.development.number
      ? null
      : (read.versions.find((version) => version.number === pin) ?? null);
  const pinnedId = pinned?.id ?? null;
  const pinnedRead = useQuery({
    queryKey: ['noyau-texts-version', email, pinnedId],
    queryFn: () => noyauTextsApi.readVersion(NOYAU_TEXTS_ID, pinnedId ?? ''),
    enabled: enabled && pinnedId !== null,
    retry: false,
  });

  return { detail: read, pinned: pinnedId === null ? undefined : pinnedRead.data?.content };
}
